import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Bank, Coins, Wallet, WarningCircle, ArrowsClockwise } from "@phosphor-icons/react";
import UnpaidInvoicesModal from "@/components/UnpaidInvoicesModal";

/**
 * TresoreriePanel — direction-only widget on the admin dashboard.
 *
 * Reads GET /api/bank-summary → total balance across bank accounts +
 * "reste à encaisser" (unpaid invoices). Backend already enforces the role
 * check (403 for technicians); we hide the panel client-side too via a
 * PriceGuard-style role gate in the parent.
 */
export default function TresoreriePanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unpaidOpen, setUnpaidOpen] = useState(false);

  const fetchIt = () => {
    setLoading(true);
    api
      .get("/bank-summary")
      .then((r) => { setData(r.data); setError(null); })
      .catch((err) => setError(formatApiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchIt(); }, []);

  const fmt = (n) =>
    (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-emerald-500/10 via-[#005CFF]/8 to-transparent border border-emerald-500/25 p-4 space-y-3"
      data-testid="tresorerie-panel"
    >
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
          <Bank size={18} weight="duotone" className="text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">Trésorerie</div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">Axonaut · temps réel</div>
        </div>
        <button
          type="button"
          onClick={fetchIt}
          disabled={loading}
          className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition-colors disabled:opacity-40"
          data-testid="tresorerie-refresh"
          aria-label="Rafraîchir"
        >
          <ArrowsClockwise size={13} weight="bold" className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {error ? (
        <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 flex items-start gap-2" data-testid="tresorerie-error">
          <WarningCircle size={16} weight="fill" className="text-red-400 shrink-0 mt-0.5" />
          <div className="text-[11px] text-red-300 flex-1">{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="h-20 rounded-xl bg-white/[0.03] border border-white/10 animate-pulse" />
          <div className="h-20 rounded-xl bg-white/[0.03] border border-white/10 animate-pulse" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-black/40 border border-emerald-500/20 p-3">
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-emerald-300/80">
                <Wallet size={11} weight="duotone" /> Total banques
              </div>
              <div
                className="font-display font-bold text-xl text-emerald-300 mt-1 tabular-nums"
                data-testid="tresorerie-total"
              >
                {fmt(data?.total_balance)}
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">
                {data?.accounts?.length || 0} compte{(data?.accounts?.length || 0) > 1 ? "s" : ""}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setUnpaidOpen(true)}
              disabled={!(data?.unpaid_count > 0)}
              className="rounded-xl bg-black/40 border border-amber-500/20 p-3 text-left w-full transition-colors hover:border-amber-500/50 hover:bg-black/60 disabled:hover:border-amber-500/20 disabled:hover:bg-black/40 disabled:cursor-default"
              data-testid="tresorerie-unpaid-btn"
              aria-label="Voir le détail des factures impayées"
            >
              <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-amber-300/80">
                <Coins size={11} weight="duotone" /> Reste à encaisser
              </div>
              <div
                className="font-display font-bold text-xl text-amber-300 mt-1 tabular-nums"
                data-testid="tresorerie-unpaid"
              >
                {fmt(data?.reste_a_encaisser)}
              </div>
              <div className="text-[10px] text-white/40 mt-0.5 flex items-center justify-between">
                <span>
                  {data?.unpaid_count || 0} facture{(data?.unpaid_count || 0) > 1 ? "s" : ""} impayée{(data?.unpaid_count || 0) > 1 ? "s" : ""}
                </span>
                {(data?.unpaid_count || 0) > 0 && (
                  <span className="text-amber-300/70 font-semibold">Détail →</span>
                )}
              </div>
            </button>
          </div>

          {(data?.accounts || []).length > 0 && (
            <div className="space-y-1 pt-1">
              {data.accounts.map((acc, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-1.5"
                  data-testid={`tresorerie-account-${i}`}
                >
                  <div className="w-1.5 h-6 rounded-full bg-emerald-400/60 shrink-0" />
                  <div className="flex-1 min-w-0 text-[11px] truncate text-white/70">
                    {acc.name || "Compte"}
                  </div>
                  <div className="font-mono text-[11px] tabular-nums text-white/90 shrink-0">
                    {fmt(acc.balance)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <UnpaidInvoicesModal open={unpaidOpen} onClose={() => setUnpaidOpen(false)} />
    </div>
  );
}
